import { RefObject, useEffect } from "react";

const LOAD_MORE_OFFSET = 200;

const scrollParentOf = (element: HTMLElement) => {
  let node = element.parentElement;

  while (node) {
    const { overflowY } = getComputedStyle(node);
    if (/(auto|scroll|overlay)/.test(overflowY)) return node;
    node = node.parentElement;
  }

  return null;
};

interface UseLoadMoreOnScrollParams {
  anchorRef: RefObject<HTMLElement>;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage: () => void;
}

export function useLoadMoreOnScroll({
  anchorRef,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}: UseLoadMoreOnScrollParams) {
  useEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor || !hasNextPage || isFetchingNextPage) return;

    const container = scrollParentOf(anchor);
    const target: HTMLElement | Window = container ?? window;

    const check = () => {
      const bottom = container
        ? container.getBoundingClientRect().bottom
        : window.innerHeight;

      if (anchor.getBoundingClientRect().top <= bottom + LOAD_MORE_OFFSET) {
        fetchNextPage();
      }
    };

    target.addEventListener("scroll", check, { passive: true });

    return () => target.removeEventListener("scroll", check);
  }, [anchorRef, hasNextPage, isFetchingNextPage, fetchNextPage]);
}
